import React, { useMemo, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Box, Grid, TextField, Typography } from '@mui/material';
import { fetchLinkedConnectors } from '../redux/actions';
import LoadingDial from '../components/loading/LoadingDial';
import SupportedConnectorCard from '../components/availableConnectorsV2/SupportedConnectorCard';
import LinkedConnectorCard from '../components/availableConnectorsV2/LinkedConnectorV2Card';
import { useCallback } from 'react';
import { Search } from '@mui/icons-material';
import ActiveGrayed from '../assets/main-logo-active.svg';

import { getLocalization, getMultipleConnectors } from '../redux/selectors';

const filterFn = (value, term) => {
  return !term || term.length < 1 || value.label.toLowerCase().includes(term.toLowerCase());
};

const FlowV2Connectors = (props) => {
  const { selectedFlow, flows, linked, supported, fetchLinkedConnectors } = props;
  const [term, setTerm] = useState('');
  const localization = getLocalization();
  const multipleConnectors = getMultipleConnectors();
  // If multiple sources are enabled, we need the hasLinkedSource flag to be false always
  const hasLinkedConnector = multipleConnectors ? false : (linked && Object.entries(linked).length > 0);

  useEffect(() => {
    if (!linked) {
      fetchLinkedConnectors();
    }
  }, [linked]);

  const byLabel = useCallback(([k1, v1], [k2, v2]) => {
    if (v1.label < v2.label) return -1;
    if (v1.label > v2.label) return 1;
    return 0;
  }, []);

  let linkedSourceComponents = useMemo(
    () =>
      Object.entries(linked ?? {})
        .sort(byLabel)
        .filter(([, value]) => filterFn(value, term))
        .map(([key, value]) => <LinkedConnectorCard key={key} connector={value} />),
    [linked, term]
  );

  let supportedConnectorComponents = useMemo(
    () =>
      Object.entries(supported ?? {})
        .filter(([key]) => linked?.[key] == undefined)
        .sort(byLabel)
        .filter(([, value]) => filterFn(value, term))
        .map(([key, value]) => (
          <SupportedConnectorCard
            key={key}
            connector={value}
            hasLinkedConnector={hasLinkedConnector}
          />
        )),
    [supported, linked, term]
  );

  const loading = !linked || !supported;
  const noSources =
    linkedSourceComponents &&
    linkedSourceComponents.length == 0 &&
    supportedConnectorComponents &&
    supportedConnectorComponents.length == 0;
  const noSourcesLabel = term
    ? (localization?.searchSource?.noSourceCaption || 'No sources found with this search term.')
    : (localization?.searchSource?.noSourceAvailableCaption || 'No sources found. Add your first source in the admin panel!')

  const icon = selectedFlow?.flowIcon || ActiveGrayed;
  return (
    <Grid container>
      <Box position="sticky" top={0} bgcolor="background.paper" width="100%" zIndex={10000}>
        <Grid item xs={12}>
          <Box display="flex" gap={1.5}>
            <Box
              component="img"
              alt={`image for ${selectedFlow?.name} flow`}
              src={icon}
              className="img"
              width={32}
              height={32}
            />
            <Box display="flex" flexDirection="column" gap={1}>
              <Typography variant="h1">{selectedFlow?.name}</Typography>
              <Typography variant="body1">{selectedFlow?.description}</Typography>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} mt={2} pb={1} display={loading || flows.processingOAuth ? 'none' : 'flex'}>
          <TextField
            name="search"
            label={localization?.searchSource?.searchSourceText || 'Search Source'}
            variant="standard"
            fullWidth
            onChange={(e) => setTerm(e.target.value)}
            InputProps={{
              endAdornment: <Search sx={{ color: '#ccc', fontSize: '16px' }}></Search>,
            }}
          />
        </Grid>
      </Box>
      {(loading || flows.processingOAuth) && (
        <Grid
          item
          xs={12}
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 'calc(600px - 200px)' }}
        >
          <LoadingDial />
        </Grid>
      )}
      <Box
        display="grid"
        sx={{
          width: '100%',
          pt: 1,
          gap: 2,
          gridTemplateColumns: {
            xs: 'repeat(1, 1fr)',
            sm: 'repeat(2, 1fr)',
          },
          '& > div:last-child': {
            mb: 2,
          },
        }}
      >
        {!loading && !flows.processingOAuth && [linkedSourceComponents, supportedConnectorComponents]}
      </Box>
      {noSources && <Typography color="text.main">{noSourcesLabel}</Typography>}
    </Grid>
  );
};

const mapStateToProps = (state, props) => {
  const flowId = state.flows.selectedFlow;

  return {
    selectedFlow: state.flows.supportedFlows[flowId],
    flows: state.flows,
    supported: state.connectors.supported[flowId],
    linked: state.connectors.linked,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    fetchLinkedConnectors: () => dispatch(fetchLinkedConnectors()),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(FlowV2Connectors);
